// ModalMudancaEtapa.tsx
import React, { useCallback, useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';

interface ModalMudancaEtapaProps {
    show: boolean;
    onHide: () => void;
    onConfirm: () => Promise<void>;
    isUpdating: boolean;
    tituloOportunidade: string;
    novaEtapaNome: string;
}

// Interface para os detalhes da mudança de etapa
export interface DetalhesMudancaEtapa {
    oportunidadeId: string | null;
    tituloOportunidade: string;
    novaEtapaId: number;
    novaEtapaNome: string;
    etapaAntigaId: number | null;
}

const ModalMudancaEtapa: React.FC<ModalMudancaEtapaProps> = ({
    show,
    onHide,
    onConfirm,
    isUpdating,
    tituloOportunidade,
    novaEtapaNome
}) => {
    // Estado local para controlar o loading
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Resetar estado quando o modal é fechado
    useEffect(() => {
        if (!show) {
            setIsSubmitting(false);
        }
    }, [show]);

    // Wrapper para garantir que o modal seja fechado após a conclusão
    const handleConfirm = useCallback(async () => {
        try {
            setIsSubmitting(true);
            await onConfirm();
            
            // Dispara um evento customizado para sinalizar conclusão
            const event = new CustomEvent('etapaUpdated', { 
                detail: { success: true }
            });
            document.dispatchEvent(event);
            
            // Fecha o modal com um pequeno delay para mostrar o sucesso
            setTimeout(() => {
                onHide();
            }, 500);
        } catch (error) {
            console.error('Erro ao atualizar etapa:', error);
            setIsSubmitting(false);
            
            // Dispara um evento customizado para sinalizar falha
            const event = new CustomEvent('etapaUpdated', { 
                detail: { success: false, error }
            });
            document.dispatchEvent(event);
        }
    }, [onConfirm, onHide]);

    // Determina se os botões devem ser desabilitados
    const isProcessing = isUpdating || isSubmitting;

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            backdrop="static"
            keyboard={false}
            enforceFocus={false}
            restoreFocus={false}
            className="modal-mudanca-etapa"
        >
            <Modal.Body>
                <div className="text-center">
                    <div className={`avatar avatar-xl ${isProcessing ? 'bg-primary-light processing-icon' : 'bg-primary-light'} rounded-circle mb-3`}>
                        <i className={`ti ti-${isProcessing ? 'loader' : 'arrow-forward'} fs-36 text-primary`} />
                    </div>
                    <h4 className="mb-2">
                        {isProcessing ? 'Processando Mudança...' : 'Alterar Etapa'}
                    </h4>
                    <p className="mb-0">
                        {isProcessing ? (
                            `Atualizando oportunidade "${tituloOportunidade}" para a etapa "${novaEtapaNome}"...`
                        ) : (
                            `Deseja mover a oportunidade "${tituloOportunidade}" para a etapa "${novaEtapaNome}"?`
                        )}
                    </p>
                    <div className="d-flex align-items-center justify-content-center mt-4">
                        <button
                            type="button"
                            className="btn btn-light me-2"
                            onClick={onHide}
                            disabled={isProcessing}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleConfirm}
                            disabled={isProcessing}
                        >
                            {isProcessing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                    Atualizando...
                                </>
                            ) : (
                                'Sim, Mover'
                            )}
                        </button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ModalMudancaEtapa;